import Vue from 'vue';
import type { Dict, Report2 } from '../types';
import type { Fzp } from './types';


// region Утилиты
export const getRowKey = (functionalGroupCode: number | null, functionalSubgroupCode: number | null, abpCode: number | null): string => {
    let result = (functionalGroupCode?.toString() ?? '');

    if (functionalSubgroupCode !== null) {
        result += `&${functionalSubgroupCode}`;
    }
    if (abpCode !== null) {
        result += `&${abpCode}`;
    }

    return result;
};
// endregion


// region Контроллеры
const scanColumns = (items: Array<Report2.Content.ColumnItem>, action: (column: Report2.Content.ColumnItem.Column) => unknown) => {
    items.forEach((item) => {
        switch (item.type) {
            case 'GROUP':
                scanColumns(item.items, action);
                break;
            case 'COLUMN':
                action(item);
                break;
        }
    });
};


/**
 * @param sheetContent содержимое листа
 *
 * @returns контроллер "Гос служба МБ"
 */
const createControllerForGovMb = (sheetContent: Fzp.ReportPart): Fzp.Controller => {
    let calcIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let ecologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthPosColumn: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeaveColumn: Report2.Content.ColumnItem.Column | null = null;
    let salaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSoColumn: Report2.Content.ColumnItem.Column | null = null;

    // Определение колонок
    scanColumns(sheetContent.rootColumnGroups, (column) => {
        switch (column.code) {
            case 'calc_income':
                calcIncomeColumn = column;
                break;
            case 'calc_total':
                calcTotalColumn = column;
                break;
            case 'ecology':
                ecologyColumn = column;
                break;
            case 'health':
                healthColumn = column;
                break;
            case 'health_pos':
                healthPosColumn = column;
                break;
            case 'mi_add_leave':
                miAddLeaveColumn = column;
                break;
            case 'salary':
                salaryColumn = column;
                break;
            case 'tax_opv':
                taxOpvColumn = column;
                break;
            case 'tax_osms':
                taxOsmsColumn = column;
                break;
            case 'tax_sn':
                taxSnColumn = column;
                break;
            case 'tax_so':
                taxSoColumn = column;
                break;
        }
    });

    return {
        isColumnEditable(column: Report2.Content.ColumnItem.Column): boolean {
            return (column.code === 'mi_add_leave') || (column.code === 'mi_reject_reason');
        },
        recalculate(cells: Array<Fzp.ReportCell>) {
            let calcIncomeCell: Fzp.ReportCell | null = null;
            let calcTotalCell: Fzp.ReportCell | null = null;
            let ecologyCell: Fzp.ReportCell | null = null;
            let healthCell: Fzp.ReportCell | null = null;
            let healthPosCell: Fzp.ReportCell | null = null;
            let miAddLeaveCell: Fzp.ReportCell | null = null;
            let salaryCell: Fzp.ReportCell | null = null;
            let taxOpvCell: Fzp.ReportCell | null = null;
            let taxOsmsCell: Fzp.ReportCell | null = null;
            let taxSnCell: Fzp.ReportCell | null = null;
            let taxSoCell: Fzp.ReportCell | null = null;

            // Определение ячеек
            for (const cell of cells) {
                switch (cell.columnKey) {
                    case (calcIncomeColumn?.key ?? null):
                        calcIncomeCell = cell;
                        break;
                    case (calcTotalColumn?.key ?? null):
                        calcTotalCell = cell;
                        break;
                    case (ecologyColumn?.key ?? null):
                        ecologyCell = cell;
                        break;
                    case (healthColumn?.key ?? null):
                        healthCell = cell;
                        break;
                    case (healthPosColumn?.key ?? null):
                        healthPosCell = cell;
                        break;
                    case (miAddLeaveColumn?.key ?? null):
                        miAddLeaveCell = cell;
                        break;
                    case (salaryColumn?.key ?? null):
                        salaryCell = cell;
                        break;
                    case (taxOpvColumn?.key ?? null):
                        taxOpvCell = cell;
                        break;
                    case (taxOsmsColumn?.key ?? null):
                        taxOsmsCell = cell;
                        break;
                    case (taxSnColumn?.key ?? null):
                        taxSnCell = cell;
                        break;
                    case (taxSoColumn?.key ?? null):
                        taxSoCell = cell;
                        break;
                }
            }

            // calc_income = сумма от [salary, health, ecology, mi_add_leave, health_pos]
            if (calcIncomeCell !== null) {
                calcIncomeCell.value = (salaryCell?.value ?? 0) + (healthCell?.value ?? 0) + (ecologyCell?.value ?? 0)
                    + (miAddLeaveCell?.value ?? 0) + (healthPosCell?.value ?? 0);
            }

            // calc_total = сумма от [calc_income, tax_sn, tax_so, tax_osms, tax_opv]
            if (calcTotalCell !== null) {
                calcTotalCell.value = (calcIncomeCell?.value ?? 0) + (taxSnCell?.value ?? 0) + (taxSoCell?.value ?? 0) + (taxOsmsCell?.value ?? 0) + (taxOpvCell?.value ?? 0);
            }
        },
    };
};

/**
 * @param sheetContent содержимое листа
 *
 * @returns контроллер "Гос служба РБ"
 */
const createControllerForGovRb = (sheetContent: Fzp.ReportPart): Fzp.Controller => {
    let calcIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let ecologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthColumn: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeaveColumn: Report2.Content.ColumnItem.Column | null = null;
    let salaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSoColumn: Report2.Content.ColumnItem.Column | null = null;

    // Определение колонок
    scanColumns(sheetContent.rootColumnGroups, (column) => {
        switch (column.code) {
            case 'calc_income':
                calcIncomeColumn = column;
                break;
            case 'calc_total':
                calcTotalColumn = column;
                break;
            case 'ecology':
                ecologyColumn = column;
                break;
            case 'health':
                healthColumn = column;
                break;
            case 'mi_add_leave':
                miAddLeaveColumn = column;
                break;
            case 'salary':
                salaryColumn = column;
                break;
            case 'tax_opv':
                taxOpvColumn = column;
                break;
            case 'tax_osms':
                taxOsmsColumn = column;
                break;
            case 'tax_sn':
                taxSnColumn = column;
                break;
            case 'tax_so':
                taxSoColumn = column;
                break;
        }
    });

    return {
        isColumnEditable(column: Report2.Content.ColumnItem.Column): boolean {
            return (column.code === 'mi_add_leave') || (column.code === 'mi_reject_reason');
        },
        recalculate(cells: Array<Fzp.ReportCell>) {
            let calcIncomeCell: Fzp.ReportCell | null = null;
            let calcTotalCell: Fzp.ReportCell | null = null;
            let ecologyCell: Fzp.ReportCell | null = null;
            let healthCell: Fzp.ReportCell | null = null;
            let miAddLeaveCell: Fzp.ReportCell | null = null;
            let salaryCell: Fzp.ReportCell | null = null;
            let taxOpvCell: Fzp.ReportCell | null = null;
            let taxOsmsCell: Fzp.ReportCell | null = null;
            let taxSnCell: Fzp.ReportCell | null = null;
            let taxSoCell: Fzp.ReportCell | null = null;

            // Определение ячеек
            for (const cell of cells) {
                switch (cell.columnKey) {
                    case (calcIncomeColumn?.key ?? null):
                        calcIncomeCell = cell;
                        break;
                    case (calcTotalColumn?.key ?? null):
                        calcTotalCell = cell;
                        break;
                    case (ecologyColumn?.key ?? null):
                        ecologyCell = cell;
                        break;
                    case (healthColumn?.key ?? null):
                        healthCell = cell;
                        break;
                    case (miAddLeaveColumn?.key ?? null):
                        miAddLeaveCell = cell;
                        break;
                    case (salaryColumn?.key ?? null):
                        salaryCell = cell;
                        break;
                    case (taxOpvColumn?.key ?? null):
                        taxOpvCell = cell;
                        break;
                    case (taxOsmsColumn?.key ?? null):
                        taxOsmsCell = cell;
                        break;
                    case (taxSnColumn?.key ?? null):
                        taxSnCell = cell;
                        break;
                    case (taxSoColumn?.key ?? null):
                        taxSoCell = cell;
                        break;
                }
            }

            // calc_income = сумма от [salary, health, ecology, mi_add_leave]
            if (calcIncomeCell !== null) {
                calcIncomeCell.value = (salaryCell?.value ?? 0) + (healthCell?.value ?? 0) + (ecologyCell?.value ?? 0) + (miAddLeaveCell?.value ?? 0);
            }

            // calc_total = сумма от [calc_income, tax_sn, tax_so, tax_osms, tax_opv]
            if (calcTotalCell !== null) {
                calcTotalCell.value = (calcIncomeCell?.value ?? 0) + (taxSnCell?.value ?? 0) + (taxSoCell?.value ?? 0) + (taxOsmsCell?.value ?? 0) + (taxOpvCell?.value ?? 0);
            }
        },
    };
};

/**
 * @param sheetContent содержимое листа
 *
 * @returns контроллер "Граждан МБ"
 */
const createControllerForCivilMb = (sheetContent: Fzp.ReportPart): Fzp.Controller => {
    let addVillageColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcEmpCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcIncomeTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcNaIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxSoColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let ecologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let empCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthPosColumn: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeave1Column: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeave2Column: Report2.Content.ColumnItem.Column | null = null;
    let miAddVillageColumn: Report2.Content.ColumnItem.Column | null = null;
    let miEcologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let miEmpCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let miHealthColumn: Report2.Content.ColumnItem.Column | null = null;
    let miHealthPosColumn: Report2.Content.ColumnItem.Column | null = null;
    let miSalaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxSoColumn: Report2.Content.ColumnItem.Column | null = null;
    let salaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSoColumn: Report2.Content.ColumnItem.Column | null = null;

    // Определение колонок
    scanColumns(sheetContent.rootColumnGroups, (column) => {
        switch (column.code) {
            case 'add_village':
                addVillageColumn = column;
                break;
            case 'calc_emp_count':
                calcEmpCountColumn = column;
                break;
            case 'calc_income':
                calcIncomeColumn = column;
                break;
            case 'calc_income_total':
                calcIncomeTotalColumn = column;
                break;
            case 'calc_na_income':
                calcNaIncomeColumn = column;
                break;
            case 'calc_tax_opv':
                calcTaxOpvColumn = column;
                break;
            case 'calc_tax_osms':
                calcTaxOsmsColumn = column;
                break;
            case 'calc_tax_sn':
                calcTaxSnColumn = column;
                break;
            case 'calc_tax_so':
                calcTaxSoColumn = column;
                break;
            case 'calc_total':
                calcTotalColumn = column;
                break;
            case 'ecology':
                ecologyColumn = column;
                break;
            case 'emp_count':
                empCountColumn = column;
                break;
            case 'health':
                healthColumn = column;
                break;
            case 'health_pos':
                healthPosColumn = column;
                break;
            case 'mi_add_leave_1':
                miAddLeave1Column = column;
                break;
            case 'mi_add_leave_2':
                miAddLeave2Column = column;
                break;
            case 'mi_add_village':
                miAddVillageColumn = column;
                break;
            case 'mi_ecology':
                miEcologyColumn = column;
                break;
            case 'mi_emp_count':
                miEmpCountColumn = column;
                break;
            case 'mi_health':
                miHealthColumn = column;
                break;
            case 'mi_health_pos':
                miHealthPosColumn = column;
                break;
            case 'mi_salary':
                miSalaryColumn = column;
                break;
            case 'mi_tax_opv':
                miTaxOpvColumn = column;
                break;
            case 'mi_tax_osms':
                miTaxOsmsColumn = column;
                break;
            case 'mi_tax_sn':
                miTaxSnColumn = column;
                break;
            case 'mi_tax_so':
                miTaxSoColumn = column;
                break;
            case 'salary':
                salaryColumn = column;
                break;
            case 'tax_opv':
                taxOpvColumn = column;
                break;
            case 'tax_osms':
                taxOsmsColumn = column;
                break;
            case 'tax_sn':
                taxSnColumn = column;
                break;
            case 'tax_so':
                taxSoColumn = column;
                break;
        }
    });

    const editableColumnCodeSet = new Set([
        'mi_add_leave_1',
        'mi_add_leave_2',
        'mi_add_village',
        'mi_ecology',
        'mi_emp_count',
        'mi_health',
        'mi_health_pos',
        'mi_reject_reason',
        'mi_salary',
        'mi_tax_sn',
        'mi_tax_so',
        'mi_tax_opv',
        'mi_tax_osms',
    ]);

    return {
        isColumnEditable(column: Report2.Content.ColumnItem.Column): boolean {
            const code = column.code;
            return (code !== null) && editableColumnCodeSet.has(code);
        },
        recalculate(cells: Array<Fzp.ReportCell>) {
            let addVillageCell: Fzp.ReportCell | null = null;
            let calcEmpCountCell: Fzp.ReportCell | null = null;
            let calcIncomeCell: Fzp.ReportCell | null = null;
            let calcIncomeTotalCell: Fzp.ReportCell | null = null;
            let calcNaIncomeCell: Fzp.ReportCell | null = null;
            let calcTaxOpvCell: Fzp.ReportCell | null = null;
            let calcTaxOsmsCell: Fzp.ReportCell | null = null;
            let calcTaxSnCell: Fzp.ReportCell | null = null;
            let calcTaxSoCell: Fzp.ReportCell | null = null;
            let calcTotalCell: Fzp.ReportCell | null = null;
            let ecologyCell: Fzp.ReportCell | null = null;
            let empCountCell: Fzp.ReportCell | null = null;
            let healthCell: Fzp.ReportCell | null = null;
            let healthPosCell: Fzp.ReportCell | null = null;
            let miAddLeave1Cell: Fzp.ReportCell | null = null;
            let miAddLeave2Cell: Fzp.ReportCell | null = null;
            let miAddVillageCell: Fzp.ReportCell | null = null;
            let miEcologyCell: Fzp.ReportCell | null = null;
            let miEmpCountCell: Fzp.ReportCell | null = null;
            let miHealthCell: Fzp.ReportCell | null = null;
            let miHealthPosCell: Fzp.ReportCell | null = null;
            let miSalaryCell: Fzp.ReportCell | null = null;
            let miTaxOpvCell: Fzp.ReportCell | null = null;
            let miTaxOsmsCell: Fzp.ReportCell | null = null;
            let miTaxSnCell: Fzp.ReportCell | null = null;
            let miTaxSoCell: Fzp.ReportCell | null = null;
            let salaryCell: Fzp.ReportCell | null = null;
            let taxOpvCell: Fzp.ReportCell | null = null;
            let taxOsmsCell: Fzp.ReportCell | null = null;
            let taxSnCell: Fzp.ReportCell | null = null;
            let taxSoCell: Fzp.ReportCell | null = null;

            // Определение ячеек
            for (const cell of cells) {
                switch (cell.columnKey) {
                    case (addVillageColumn?.key ?? null):
                        addVillageCell = cell;
                        break;
                    case (calcEmpCountColumn?.key ?? null):
                        calcEmpCountCell = cell;
                        break;
                    case (calcIncomeColumn?.key ?? null):
                        calcIncomeCell = cell;
                        break;
                    case (calcIncomeTotalColumn?.key ?? null):
                        calcIncomeTotalCell = cell;
                        break;
                    case (calcNaIncomeColumn?.key ?? null):
                        calcNaIncomeCell = cell;
                        break;
                    case (calcTaxOpvColumn?.key ?? null):
                        calcTaxOpvCell = cell;
                        break;
                    case (calcTaxOsmsColumn?.key ?? null):
                        calcTaxOsmsCell = cell;
                        break;
                    case (calcTaxSnColumn?.key ?? null):
                        calcTaxSnCell = cell;
                        break;
                    case (calcTaxSoColumn?.key ?? null):
                        calcTaxSoCell = cell;
                        break;
                    case (calcTotalColumn?.key ?? null):
                        calcTotalCell = cell;
                        break;
                    case (ecologyColumn?.key ?? null):
                        ecologyCell = cell;
                        break;
                    case (empCountColumn?.key ?? null):
                        empCountCell = cell;
                        break;
                    case (healthColumn?.key ?? null):
                        healthCell = cell;
                        break;
                    case (healthPosColumn?.key ?? null):
                        healthPosCell = cell;
                        break;
                    case (miAddLeave1Column?.key ?? null):
                        miAddLeave1Cell = cell;
                        break;
                    case (miAddLeave2Column?.key ?? null):
                        miAddLeave2Cell = cell;
                        break;
                    case (miAddVillageColumn?.key ?? null):
                        miAddVillageCell = cell;
                        break;
                    case (miEcologyColumn?.key ?? null):
                        miEcologyCell = cell;
                        break;
                    case (miEmpCountColumn?.key ?? null):
                        miEmpCountCell = cell;
                        break;
                    case (miHealthColumn?.key ?? null):
                        miHealthCell = cell;
                        break;
                    case (miHealthPosColumn?.key ?? null):
                        miHealthPosCell = cell;
                        break;
                    case (miSalaryColumn?.key ?? null):
                        miSalaryCell = cell;
                        break;
                    case (miTaxOpvColumn?.key ?? null):
                        miTaxOpvCell = cell;
                        break;
                    case (miTaxOsmsColumn?.key ?? null):
                        miTaxOsmsCell = cell;
                        break;
                    case (miTaxSnColumn?.key ?? null):
                        miTaxSnCell = cell;
                        break;
                    case (miTaxSoColumn?.key ?? null):
                        miTaxSoCell = cell;
                        break;
                    case (salaryColumn?.key ?? null):
                        salaryCell = cell;
                        break;
                    case (taxOpvColumn?.key ?? null):
                        taxOpvCell = cell;
                        break;
                    case (taxOsmsColumn?.key ?? null):
                        taxOsmsCell = cell;
                        break;
                    case (taxSnColumn?.key ?? null):
                        taxSnCell = cell;
                        break;
                    case (taxSoColumn?.key ?? null):
                        taxSoCell = cell;
                        break;
                }
            }

            // calc_na_income = сумма от [mi_salary, mi_add_village, mi_health, mi_ecology, mi_add_leave_2, mi_health_pos]
            if (calcNaIncomeCell !== null) {
                calcNaIncomeCell.value = (miSalaryCell?.value ?? 0) + (miAddVillageCell?.value ?? 0) + (miHealthCell?.value ?? 0)
                    + (miEcologyCell?.value ?? 0) + (miAddLeave2Cell?.value ?? 0) + (miHealthPosCell?.value ?? 0);
            }

            // calc_income = сумма от [salary, add_village, health, ecology, mi_add_leave_1, health_pos]
            if (calcIncomeCell !== null) {
                calcIncomeCell.value = (salaryCell?.value ?? 0) + (addVillageCell?.value ?? 0) + (healthCell?.value ?? 0)
                    + (ecologyCell?.value ?? 0) + (miAddLeave1Cell?.value ?? 0) + (healthPosCell?.value ?? 0);
            }

            // calc_tax_opv = сумма от [tax_opv, mi_tax_opv]
            if (calcTaxOpvCell !== null) {
                calcTaxOpvCell.value = (taxOpvCell?.value ?? 0) + (miTaxOpvCell?.value ?? 0);
            }

            // calc_tax_osms = сумма от [tax_osms, mi_tax_osms]
            if (calcTaxOsmsCell !== null) {
                calcTaxOsmsCell.value = (taxOsmsCell?.value ?? 0) + (miTaxOsmsCell?.value ?? 0);
            }

            // calc_tax_so = сумма от [tax_so, mi_tax_so]
            if (calcTaxSoCell !== null) {
                calcTaxSoCell.value = (taxSoCell?.value ?? 0) + (miTaxSoCell?.value ?? 0);
            }

            // calc_tax_sn = сумма от [tax_sn, mi_tax_sn]
            if (calcTaxSnCell !== null) {
                calcTaxSnCell.value = (taxSnCell?.value ?? 0) + (miTaxSnCell?.value ?? 0);
            }

            // calc_income_total = сумма от [calc_income, calc_na_income]
            if (calcIncomeTotalCell !== null) {
                calcIncomeTotalCell.value = (calcIncomeCell?.value ?? 0) + (calcNaIncomeCell?.value ?? 0);
            }

            // calc_total = сумма от [calc_income_total, calc_tax_sn, calc_tax_so, calc_tax_osms, calc_tax_opv]
            if (calcTotalCell !== null) {
                calcTotalCell.value = (calcIncomeTotalCell?.value ?? 0) + (calcTaxSnCell?.value ?? 0) + (calcTaxSoCell?.value ?? 0)
                    + (calcTaxOsmsCell?.value ?? 0) + (calcTaxOpvCell?.value ?? 0);
            }

            // calc_emp_count = сумма от [emp_count, mi_emp_count]
            if (calcEmpCountCell !== null) {
                calcEmpCountCell.value = (empCountCell?.value ?? 0) + (miEmpCountCell?.value ?? 0);
            }
        },
    };
};

/**
 * @param sheetContent содержимое листа
 *
 * @returns контроллер "Граждан РБ"
 */
const createControllerForCivilRb = (sheetContent: Fzp.ReportPart): Fzp.Controller => {
    let addVillageColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcEmpCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcIncomeTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcNaIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTaxSoColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let ecologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let empCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthPosColumn: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeave1Column: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeave2Column: Report2.Content.ColumnItem.Column | null = null;
    let miAddVillageColumn: Report2.Content.ColumnItem.Column | null = null;
    let miEcologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let miEmpCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let miHealthColumn: Report2.Content.ColumnItem.Column | null = null;
    let miSalaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let miTaxSoColumn: Report2.Content.ColumnItem.Column | null = null;
    let salaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSoColumn: Report2.Content.ColumnItem.Column | null = null;

    // Определение колонок
    scanColumns(sheetContent.rootColumnGroups, (column) => {
        switch (column.code) {
            case 'add_village':
                addVillageColumn = column;
                break;
            case 'calc_emp_count':
                calcEmpCountColumn = column;
                break;
            case 'calc_income':
                calcIncomeColumn = column;
                break;
            case 'calc_income_total':
                calcIncomeTotalColumn = column;
                break;
            case 'calc_na_income':
                calcNaIncomeColumn = column;
                break;
            case 'calc_tax_opv':
                calcTaxOpvColumn = column;
                break;
            case 'calc_tax_osms':
                calcTaxOsmsColumn = column;
                break;
            case 'calc_tax_sn':
                calcTaxSnColumn = column;
                break;
            case 'calc_tax_so':
                calcTaxSoColumn = column;
                break;
            case 'calc_total':
                calcTotalColumn = column;
                break;
            case 'ecology':
                ecologyColumn = column;
                break;
            case 'emp_count':
                empCountColumn = column;
                break;
            case 'health':
                healthColumn = column;
                break;
            case 'health_pos':
                healthPosColumn = column;
                break;
            case 'mi_add_leave_1':
                miAddLeave1Column = column;
                break;
            case 'mi_add_leave_2':
                miAddLeave2Column = column;
                break;
            case 'mi_add_village':
                miAddVillageColumn = column;
                break;
            case 'mi_ecology':
                miEcologyColumn = column;
                break;
            case 'mi_emp_count':
                miEmpCountColumn = column;
                break;
            case 'mi_health':
                miHealthColumn = column;
                break;
            case 'mi_salary':
                miSalaryColumn = column;
                break;
            case 'mi_tax_opv':
                miTaxOpvColumn = column;
                break;
            case 'mi_tax_osms':
                miTaxOsmsColumn = column;
                break;
            case 'mi_tax_sn':
                miTaxSnColumn = column;
                break;
            case 'mi_tax_so':
                miTaxSoColumn = column;
                break;
            case 'salary':
                salaryColumn = column;
                break;
            case 'tax_opv':
                taxOpvColumn = column;
                break;
            case 'tax_osms':
                taxOsmsColumn = column;
                break;
            case 'tax_sn':
                taxSnColumn = column;
                break;
            case 'tax_so':
                taxSoColumn = column;
                break;
        }
    });

    const editableColumnCodeSet = new Set([
        'mi_add_leave_1',
        'mi_add_leave_2',
        'mi_add_village',
        'mi_ecology',
        'mi_emp_count',
        'mi_health',
        'mi_reject_reason',
        'mi_salary',
        'mi_tax_sn',
        'mi_tax_so',
        'mi_tax_opv',
        'mi_tax_osms',
    ]);

    return {
        isColumnEditable(column: Report2.Content.ColumnItem.Column): boolean {
            const code = column.code;
            return (code !== null) && editableColumnCodeSet.has(code);
        },
        recalculate(cells: Array<Fzp.ReportCell>) {
            let addVillageCell: Fzp.ReportCell | null = null;
            let calcEmpCountCell: Fzp.ReportCell | null = null;
            let calcIncomeCell: Fzp.ReportCell | null = null;
            let calcIncomeTotalCell: Fzp.ReportCell | null = null;
            let calcNaIncomeCell: Fzp.ReportCell | null = null;
            let calcTaxOpvCell: Fzp.ReportCell | null = null;
            let calcTaxOsmsCell: Fzp.ReportCell | null = null;
            let calcTaxSnCell: Fzp.ReportCell | null = null;
            let calcTaxSoCell: Fzp.ReportCell | null = null;
            let calcTotalCell: Fzp.ReportCell | null = null;
            let ecologyCell: Fzp.ReportCell | null = null;
            let empCountCell: Fzp.ReportCell | null = null;
            let healthCell: Fzp.ReportCell | null = null;
            let healthPosCell: Fzp.ReportCell | null = null;
            let miAddLeave1Cell: Fzp.ReportCell | null = null;
            let miAddLeave2Cell: Fzp.ReportCell | null = null;
            let miAddVillageCell: Fzp.ReportCell | null = null;
            let miEcologyCell: Fzp.ReportCell | null = null;
            let miEmpCountCell: Fzp.ReportCell | null = null;
            let miHealthCell: Fzp.ReportCell | null = null;
            let miSalaryCell: Fzp.ReportCell | null = null;
            let miTaxOpvCell: Fzp.ReportCell | null = null;
            let miTaxOsmsCell: Fzp.ReportCell | null = null;
            let miTaxSnCell: Fzp.ReportCell | null = null;
            let miTaxSoCell: Fzp.ReportCell | null = null;
            let salaryCell: Fzp.ReportCell | null = null;
            let taxOpvCell: Fzp.ReportCell | null = null;
            let taxOsmsCell: Fzp.ReportCell | null = null;
            let taxSnCell: Fzp.ReportCell | null = null;
            let taxSoCell: Fzp.ReportCell | null = null;

            // Определение ячеек
            for (const cell of cells) {
                switch (cell.columnKey) {
                    case (addVillageColumn?.key ?? null):
                        addVillageCell = cell;
                        break;
                    case (calcEmpCountColumn?.key ?? null):
                        calcEmpCountCell = cell;
                        break;
                    case (calcIncomeColumn?.key ?? null):
                        calcIncomeCell = cell;
                        break;
                    case (calcIncomeTotalColumn?.key ?? null):
                        calcIncomeTotalCell = cell;
                        break;
                    case (calcNaIncomeColumn?.key ?? null):
                        calcNaIncomeCell = cell;
                        break;
                    case (calcTaxOpvColumn?.key ?? null):
                        calcTaxOpvCell = cell;
                        break;
                    case (calcTaxOsmsColumn?.key ?? null):
                        calcTaxOsmsCell = cell;
                        break;
                    case (calcTaxSnColumn?.key ?? null):
                        calcTaxSnCell = cell;
                        break;
                    case (calcTaxSoColumn?.key ?? null):
                        calcTaxSoCell = cell;
                        break;
                    case (calcTotalColumn?.key ?? null):
                        calcTotalCell = cell;
                        break;
                    case (ecologyColumn?.key ?? null):
                        ecologyCell = cell;
                        break;
                    case (empCountColumn?.key ?? null):
                        empCountCell = cell;
                        break;
                    case (healthColumn?.key ?? null):
                        healthCell = cell;
                        break;
                    case (healthPosColumn?.key ?? null):
                        healthPosCell = cell;
                        break;
                    case (miAddLeave1Column?.key ?? null):
                        miAddLeave1Cell = cell;
                        break;
                    case (miAddLeave2Column?.key ?? null):
                        miAddLeave2Cell = cell;
                        break;
                    case (miAddVillageColumn?.key ?? null):
                        miAddVillageCell = cell;
                        break;
                    case (miEcologyColumn?.key ?? null):
                        miEcologyCell = cell;
                        break;
                    case (miEmpCountColumn?.key ?? null):
                        miEmpCountCell = cell;
                        break;
                    case (miHealthColumn?.key ?? null):
                        miHealthCell = cell;
                        break;
                    case (miSalaryColumn?.key ?? null):
                        miSalaryCell = cell;
                        break;
                    case (miTaxOpvColumn?.key ?? null):
                        miTaxOpvCell = cell;
                        break;
                    case (miTaxOsmsColumn?.key ?? null):
                        miTaxOsmsCell = cell;
                        break;
                    case (miTaxSnColumn?.key ?? null):
                        miTaxSnCell = cell;
                        break;
                    case (miTaxSoColumn?.key ?? null):
                        miTaxSoCell = cell;
                        break;
                    case (salaryColumn?.key ?? null):
                        salaryCell = cell;
                        break;
                    case (taxOpvColumn?.key ?? null):
                        taxOpvCell = cell;
                        break;
                    case (taxOsmsColumn?.key ?? null):
                        taxOsmsCell = cell;
                        break;
                    case (taxSnColumn?.key ?? null):
                        taxSnCell = cell;
                        break;
                    case (taxSoColumn?.key ?? null):
                        taxSoCell = cell;
                        break;
                }
            }

            // calc_na_income = сумма от [mi_salary, mi_add_village, mi_health, mi_ecology, mi_add_leave_2, mi_tax_sn]
            if (calcNaIncomeCell !== null) {
                calcNaIncomeCell.value = (miSalaryCell?.value ?? 0) + (miAddVillageCell?.value ?? 0) + (miHealthCell?.value ?? 0)
                    + (miEcologyCell?.value ?? 0) + (miAddLeave2Cell?.value ?? 0) + (miTaxSnCell?.value ?? 0);
            }

            // calc_income = сумма от [salary, add_village, health, ecology, mi_add_leave_1, health_pos]
            if (calcIncomeCell !== null) {
                calcIncomeCell.value = (salaryCell?.value ?? 0) + (addVillageCell?.value ?? 0) + (healthCell?.value ?? 0)
                    + (ecologyCell?.value ?? 0) + (miAddLeave1Cell?.value ?? 0) + (healthPosCell?.value ?? 0);
            }

            // calc_tax_opv = сумма от [tax_opv, mi_tax_opv]
            if (calcTaxOpvCell !== null) {
                calcTaxOpvCell.value = (taxOpvCell?.value ?? 0) + (miTaxOpvCell?.value ?? 0);
            }

            // calc_tax_osms = сумма от [tax_osms, mi_tax_osms]
            if (calcTaxOsmsCell !== null) {
                calcTaxOsmsCell.value = (taxOsmsCell?.value ?? 0) + (miTaxOsmsCell?.value ?? 0);
            }

            // calc_tax_so = сумма от [tax_so, mi_tax_so]
            if (calcTaxSoCell !== null) {
                calcTaxSoCell.value = (taxSoCell?.value ?? 0) + (miTaxSoCell?.value ?? 0);
            }

            // calc_tax_sn = сумма от [tax_sn, mi_tax_sn]
            if (calcTaxSnCell !== null) {
                calcTaxSnCell.value = (taxSnCell?.value ?? 0) + (miTaxSnCell?.value ?? 0);
            }

            // calc_income_total = сумма от [calc_income, calc_na_income]
            if (calcIncomeTotalCell !== null) {
                calcIncomeTotalCell.value = (calcIncomeCell?.value ?? 0) + (calcNaIncomeCell?.value ?? 0);
            }

            // calc_total = сумма от [calc_income_total, calc_tax_sn, calc_tax_so, calc_tax_osms, calc_tax_opv]
            if (calcTotalCell !== null) {
                calcTotalCell.value = (calcIncomeTotalCell?.value ?? 0) + (calcTaxSnCell?.value ?? 0) + (calcTaxSoCell?.value ?? 0)
                    + (calcTaxOsmsCell?.value ?? 0) + (calcTaxOpvCell?.value ?? 0);
            }

            // calc_emp_count = сумма от [emp_count, mi_emp_count]
            if (calcEmpCountCell !== null) {
                calcEmpCountCell.value = (empCountCell?.value ?? 0) + (miEmpCountCell?.value ?? 0);
            }
        },
    };
};

/**
 * @param sheetContent содержимое листа
 *
 * @returns контроллер "правоохранит.органы МБ"
 */
const createControllerForMvdMb = (sheetContent: Fzp.ReportPart): Fzp.Controller => {
    let calcIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let ecologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let empCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthPosColumn: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeaveColumn: Report2.Content.ColumnItem.Column | null = null;
    let salaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSoColumn: Report2.Content.ColumnItem.Column | null = null;

    // Определение колонок
    scanColumns(sheetContent.rootColumnGroups, (column) => {
        switch (column.code) {
            case 'calc_income':
                calcIncomeColumn = column;
                break;
            case 'calc_total':
                calcTotalColumn = column;
                break;
            case 'ecology':
                ecologyColumn = column;
                break;
            case 'emp_count':
                empCountColumn = column;
                break;
            case 'health':
                healthColumn = column;
                break;
            case 'health_pos':
                healthPosColumn = column;
                break;
            case 'mi_add_leave':
                miAddLeaveColumn = column;
                break;
            case 'salary':
                salaryColumn = column;
                break;
            case 'tax_opv':
                taxOpvColumn = column;
                break;
            case 'tax_osms':
                taxOsmsColumn = column;
                break;
            case 'tax_sn':
                taxSnColumn = column;
                break;
            case 'tax_so':
                taxSoColumn = column;
                break;
        }
    });

    return {
        isColumnEditable(column: Report2.Content.ColumnItem.Column): boolean {
            return (column.code === 'mi_add_leave') || (column.code === 'mi_reject_reason');
        },
        recalculate(cells: Array<Fzp.ReportCell>) {
            let calcIncomeCell: Fzp.ReportCell | null = null;
            let calcTotalCell: Fzp.ReportCell | null = null;
            let ecologyCell: Fzp.ReportCell | null = null;
            let healthCell: Fzp.ReportCell | null = null;
            let healthPosCell: Fzp.ReportCell | null = null;
            let miAddLeaveCell: Fzp.ReportCell | null = null;
            let salaryCell: Fzp.ReportCell | null = null;
            let taxOpvCell: Fzp.ReportCell | null = null;
            let taxOsmsCell: Fzp.ReportCell | null = null;
            let taxSnCell: Fzp.ReportCell | null = null;
            let taxSoCell: Fzp.ReportCell | null = null;

            // Определение ячеек
            for (const cell of cells) {
                switch (cell.columnKey) {
                    case (calcIncomeColumn?.key ?? null):
                        calcIncomeCell = cell;
                        break;
                    case (calcTotalColumn?.key ?? null):
                        calcTotalCell = cell;
                        break;
                    case (ecologyColumn?.key ?? null):
                        ecologyCell = cell;
                        break;
                    case (healthColumn?.key ?? null):
                        healthCell = cell;
                        break;
                    case (healthPosColumn?.key ?? null):
                        healthPosCell = cell;
                        break;
                    case (miAddLeaveColumn?.key ?? null):
                        miAddLeaveCell = cell;
                        break;
                    case (salaryColumn?.key ?? null):
                        salaryCell = cell;
                        break;
                    case (taxOpvColumn?.key ?? null):
                        taxOpvCell = cell;
                        break;
                    case (taxOsmsColumn?.key ?? null):
                        taxOsmsCell = cell;
                        break;
                    case (taxSnColumn?.key ?? null):
                        taxSnCell = cell;
                        break;
                    case (taxSoColumn?.key ?? null):
                        taxSoCell = cell;
                        break;
                }
            }

            // calc_income = сумма от [salary, health, ecology, mi_add_leave, health_pos]
            if (calcIncomeCell !== null) {
                calcIncomeCell.value = (salaryCell?.value ?? 0) + (healthCell?.value ?? 0) + (ecologyCell?.value ?? 0)
                    + (miAddLeaveCell?.value ?? 0) + (healthPosCell?.value ?? 0);
            }

            // calc_total = сумма от [calc_income, tax_sn, tax_so, tax_osms, tax_opv]
            if (calcTotalCell !== null) {
                calcTotalCell.value = (calcIncomeCell?.value ?? 0) + (taxSnCell?.value ?? 0) + (taxSoCell?.value ?? 0)
                    + (taxOsmsCell?.value ?? 0) + (taxOpvCell?.value ?? 0);
            }
        },
    };
};

/**
 * @param sheetContent содержимое листа
 *
 * @returns контроллер "правоохранит.органы РБ"
 */
const createControllerForMvdRb = (sheetContent: Fzp.ReportPart): Fzp.Controller => {
    let calcIncomeColumn: Report2.Content.ColumnItem.Column | null = null;
    let calcTotalColumn: Report2.Content.ColumnItem.Column | null = null;
    let ecologyColumn: Report2.Content.ColumnItem.Column | null = null;
    let empCountColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthColumn: Report2.Content.ColumnItem.Column | null = null;
    let healthPosColumn: Report2.Content.ColumnItem.Column | null = null;
    let miAddLeaveColumn: Report2.Content.ColumnItem.Column | null = null;
    let salaryColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOpvColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxOsmsColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSnColumn: Report2.Content.ColumnItem.Column | null = null;
    let taxSoColumn: Report2.Content.ColumnItem.Column | null = null;

    // Определение колонок
    scanColumns(sheetContent.rootColumnGroups, (column) => {
        switch (column.code) {
            case 'calc_income':
                calcIncomeColumn = column;
                break;
            case 'calc_total':
                calcTotalColumn = column;
                break;
            case 'ecology':
                ecologyColumn = column;
                break;
            case 'emp_count':
                empCountColumn = column;
                break;
            case 'health':
                healthColumn = column;
                break;
            case 'health_pos':
                healthPosColumn = column;
                break;
            case 'mi_add_leave':
                miAddLeaveColumn = column;
                break;
            case 'salary':
                salaryColumn = column;
                break;
            case 'tax_opv':
                taxOpvColumn = column;
                break;
            case 'tax_osms':
                taxOsmsColumn = column;
                break;
            case 'tax_sn':
                taxSnColumn = column;
                break;
            case 'tax_so':
                taxSoColumn = column;
                break;
        }
    });

    return {
        isColumnEditable(column: Report2.Content.ColumnItem.Column): boolean {
            return (column.code === 'mi_add_leave') || (column.code === 'mi_reject_reason');
        },
        recalculate(cells: Array<Fzp.ReportCell>) {
            let calcIncomeCell: Fzp.ReportCell | null = null;
            let calcTotalCell: Fzp.ReportCell | null = null;
            let ecologyCell: Fzp.ReportCell | null = null;
            let healthCell: Fzp.ReportCell | null = null;
            let healthPosCell: Fzp.ReportCell | null = null;
            let miAddLeaveCell: Fzp.ReportCell | null = null;
            let salaryCell: Fzp.ReportCell | null = null;
            let taxOpvCell: Fzp.ReportCell | null = null;
            let taxOsmsCell: Fzp.ReportCell | null = null;
            let taxSnCell: Fzp.ReportCell | null = null;
            let taxSoCell: Fzp.ReportCell | null = null;

            // Определение ячеек
            for (const cell of cells) {
                switch (cell.columnKey) {
                    case (calcIncomeColumn?.key ?? null):
                        calcIncomeCell = cell;
                        break;
                    case (calcTotalColumn?.key ?? null):
                        calcTotalCell = cell;
                        break;
                    case (ecologyColumn?.key ?? null):
                        ecologyCell = cell;
                        break;
                    case (healthColumn?.key ?? null):
                        healthCell = cell;
                        break;
                    case (healthPosColumn?.key ?? null):
                        healthPosCell = cell;
                        break;
                    case (miAddLeaveColumn?.key ?? null):
                        miAddLeaveCell = cell;
                        break;
                    case (salaryColumn?.key ?? null):
                        salaryCell = cell;
                        break;
                    case (taxOpvColumn?.key ?? null):
                        taxOpvCell = cell;
                        break;
                    case (taxOsmsColumn?.key ?? null):
                        taxOsmsCell = cell;
                        break;
                    case (taxSnColumn?.key ?? null):
                        taxSnCell = cell;
                        break;
                    case (taxSoColumn?.key ?? null):
                        taxSoCell = cell;
                        break;
                }
            }

            // calc_income = сумма от [salary, health, ecology, mi_add_leave, health_pos]
            if (calcIncomeCell !== null) {
                calcIncomeCell.value = (salaryCell?.value ?? 0) + (healthCell?.value ?? 0) + (ecologyCell?.value ?? 0)
                    + (miAddLeaveCell?.value ?? 0) + (healthPosCell?.value ?? 0);
            }

            // calc_total = сумма от [calc_income, tax_sn, tax_so, tax_osms, tax_opv]
            if (calcTotalCell !== null) {
                calcTotalCell.value = (calcIncomeCell?.value ?? 0) + (taxSnCell?.value ?? 0) + (taxSoCell?.value ?? 0)
                    + (taxOsmsCell?.value ?? 0) + (taxOpvCell?.value ?? 0);
            }
        },
    };
};

export const createController = (reportSheet: Fzp.ReportSheet, sheetContent: Fzp.ReportPart): Fzp.Controller => {
    switch (reportSheet) {
        case 'GOV_MB':
            return createControllerForGovMb(sheetContent);
        case 'GOV_RB':
            return createControllerForGovRb(sheetContent);
        case 'CIVIL_MB':
            return createControllerForCivilMb(sheetContent);
        case 'CIVIL_RB':
            return createControllerForCivilRb(sheetContent);
        case 'MVD_MB':
            return createControllerForMvdMb(sheetContent);
        case 'MVD_RB':
            return createControllerForMvdRb(sheetContent);
        default:
            throw new Error(`Unknown report sheet: ${reportSheet}`);
    }
};
// endregion


// region Реактивное содержимое отчета
const createReactiveReportCell = (original: Fzp.ReportCell): Fzp.ReactiveReportCell => {
    const state = Vue.observable({
        original,
        columnKey: original.columnKey,
        value: original.value,
    });

    const result: Fzp.ReactiveReportCell = {
        get columnKey(): string {
            return state.columnKey;
        },
        set columnKey(value: string) {
            state.columnKey = value;
        },

        get value(): number {
            return state.value;
        },
        set value(value: number) {
            state.value = value;
        },

        get isChanged(): boolean {
            return (state.value !== state.original.value) || (state.columnKey !== state.original.columnKey);
        },

        get original(): Fzp.ReportCell {
            return state.original;
        },

        reset(): void {
            state.value = state.original.value;
            state.columnKey = state.original.columnKey;
        },

        createPlainCopy(canUseOriginals?: boolean): Fzp.ReportCell {
            if (canUseOriginals && (!result.isChanged)) {
                return state.original;
            } else {
                return {
                    columnKey: state.columnKey,
                    value: state.value,
                };
            }
        },

        markAsUnchanged(): Fzp.ReportCell {
            if (result.isChanged) {
                state.original = result.createPlainCopy(true);
            }
            return state.original;
        },
    };

    return result;
};

const createReactiveReportRow = (original: Fzp.ReportRow): Fzp.ReactiveReportRow => {
    const cells: Array<Fzp.ReactiveReportCell> = original.cells.map(createReactiveReportCell);

    const state = Vue.observable({
        original,
        functionalGroupCode: original.functionalGroupCode,
        functionalSubgroupCode: original.functionalSubgroupCode,
        abpCode: original.abpCode,
    });

    const result: Fzp.ReactiveReportRow = {
        get functionalGroupCode(): number {
            return state.functionalGroupCode;
        },
        set functionalGroupCode(value: number) {
            state.functionalGroupCode = value;
        },

        get functionalSubgroupCode(): number {
            return state.functionalSubgroupCode;
        },
        set functionalSubgroupCode(value: number) {
            state.functionalSubgroupCode = value;
        },

        get abpCode(): number {
            return state.abpCode;
        },
        set abpCode(value: number) {
            state.abpCode = value;
        },

        get cells(): Array<Fzp.ReactiveReportCell> {
            return cells;
        },

        get isChanged(): boolean {
            if (
                (state.original.functionalGroupCode !== state.functionalGroupCode)
                ||
                (state.original.functionalSubgroupCode !== state.functionalSubgroupCode)
                ||
                (state.original.abpCode !== state.abpCode)
            ) {
                return true;
            }

            for (const cell of cells) {
                if (cell.isChanged) {
                    return true;
                }
            }

            return false;
        },

        get original(): Fzp.ReportRow {
            return state.original;
        },

        reset(): void {
            cells.forEach((cell) => {
                cell.reset();
            });

            state.functionalGroupCode = state.original.functionalGroupCode;
            state.functionalSubgroupCode = state.original.functionalSubgroupCode;
            state.abpCode = state.original.abpCode;
        },

        createPlainCopy(canUseOriginals?: boolean): Fzp.ReportRow {
            if (canUseOriginals && (!result.isChanged)) {
                return state.original;
            } else {
                return {
                    functionalGroupCode: state.functionalGroupCode,
                    functionalSubgroupCode: state.functionalSubgroupCode,
                    abpCode: state.abpCode,
                    cells: cells.map((cell) => cell.createPlainCopy(true)),
                };
            }
        },

        markAsUnchanged(): Fzp.ReportRow {
            if (result.isChanged) {
                for (const cell of cells) {
                    cell.markAsUnchanged();
                }

                state.original = result.createPlainCopy(true);
            }
            return state.original;
        },
    };
    return result;
};

const createReactiveReportPart = (original: Fzp.ReportPart): Fzp.ReactiveReportPart => {
    const rows: Array<Fzp.ReactiveReportRow> = original.rows.map(createReactiveReportRow);

    const state = Vue.observable({
        original,
        originalRejectReasonsMap: new Map<string, Fzp.ReportRejectReason>(),
        rejectReasonsMap: new Map<string, Fzp.ReportRejectReason>(),
    });

    const getRejectReason = (rowKey: string): Fzp.ReportRejectReason | null => {
        return state.rejectReasonsMap.get(rowKey) ?? null;
    };

    const setRejectReason = (
        functionalGroupCode: number | null,
        functionalSubgroupCode: number | null,
        abpCode: number | null,
        kk: string | null,
        ru: string | null,
    ): void => {
        let actualKk = kk;
        if (actualKk !== null) {
            actualKk = actualKk.trim();
            if (actualKk.length === 0) {
                actualKk = null;
            }
        }

        let actualRu = ru;
        if (actualRu !== null) {
            actualRu = actualRu.trim();
            if (actualRu.length === 0) {
                actualRu = null;
            }
        }

        const rowKey = getRowKey(functionalGroupCode, functionalSubgroupCode, abpCode);

        const oldRejectReason = getRejectReason(rowKey);
        if ((oldRejectReason?.kk !== actualKk) || (actualRu !== oldRejectReason?.ru)) {
            const newRejectReasonsMap = new Map(state.rejectReasonsMap);

            if ((actualKk === null) && (actualRu === null)) {
                newRejectReasonsMap.delete(rowKey);
            } else {
                newRejectReasonsMap.set(
                    rowKey,
                    {
                        functionalGroupCode: functionalGroupCode,
                        functionalSubgroupCode: functionalSubgroupCode,
                        abpCode: abpCode,
                        kk: actualKk,
                        ru: actualRu,
                    },
                );
            }

            state.rejectReasonsMap = newRejectReasonsMap;
        }
    };

    const isRejectReasonsEqual = (reason1: Fzp.ReportRejectReason | null, reason2: Fzp.ReportRejectReason | null) => {
        if (reason1 === null) {
            return (reason2 === null);
        } else {
            if (reason2 === null) {
                return false;
            } else {
                return (
                    (reason1.kk === reason2.kk)
                    ||
                    (reason1.ru === reason2.ru)
                );
            }
        }
    };

    const isRejectReasonChanged = (rowKey: string) => {
        return !isRejectReasonsEqual(
            state.originalRejectReasonsMap.get(rowKey) ?? null,
            state.rejectReasonsMap.get(rowKey) ?? null,
        );
    };


    // region Заполнение карт обоснований для отклонения
    (() => {
        const rejectReasons = original.rejectReasons;
        if ((rejectReasons !== null) && (rejectReasons !== undefined)) {
            for (const rejectReason of rejectReasons) {
                const rowKey = getRowKey(rejectReason.functionalGroupCode, rejectReason.functionalSubgroupCode, rejectReason.abpCode);
                state.originalRejectReasonsMap.set(rowKey, rejectReason);
            }
        }

        state.rejectReasonsMap = state.originalRejectReasonsMap;
    })();
    // endregion

    const result: Fzp.ReactiveReportPart = {
        get functionalGroupList(): Array<Dict.EbkFunc> {
            return state.original.functionalGroupList;
        },

        get functionalSubgroupList(): Array<Dict.EbkFunc> {
            return state.original.functionalSubgroupList;
        },

        get abpList(): Array<Dict.EbkFunc> {
            return state.original.abpList;
        },

        get rootColumnGroups(): Array<Report2.Content.ColumnItem.ColumnGroup> {
            return state.original.rootColumnGroups;
        },

        get rows(): Array<Fzp.ReactiveReportRow> {
            return rows;
        },

        get rejectReasons(): Array<Fzp.ReportRejectReason> | null {
            if (state.rejectReasonsMap.size === 0) {
                return null;
            } else {
                return [...state.rejectReasonsMap.values()];
            }
        },

        get isChanged(): boolean {
            // Проверка изменений в строках отчета
            for (const row of rows) {
                if (row.isChanged) {
                    return true;
                }
            }

            // Проверка изменений в обоснованиях отклонения
            if (state.rejectReasonsMap.size === state.originalRejectReasonsMap.size) {
                for (const [rowKey, rejectReason] of state.rejectReasonsMap) {
                    const originalRejectReason = state.originalRejectReasonsMap.get(rowKey) ?? null;
                    if (!isRejectReasonsEqual(originalRejectReason, rejectReason)) {
                        return true;
                    }
                }
            } else {
                return true;
            }

            return false;
        },

        get original(): Fzp.ReportPart {
            return state.original;
        },

        reset(): void {
            rows.forEach((row) => {
                row.reset();
            });
        },

        createPlainCopy(canUseOriginals?: boolean): Fzp.ReportPart {
            if (canUseOriginals && (!result.isChanged)) {
                return state.original;
            } else {
                let rejectReasons: Array<Fzp.ReportRejectReason> | null;
                if (state.rejectReasonsMap.size === 0) {
                    rejectReasons = null;
                } else {
                    rejectReasons = [...state.rejectReasonsMap.values()];
                }

                return {
                    functionalGroupList: state.original.functionalGroupList,
                    functionalSubgroupList: state.original.functionalSubgroupList,
                    abpList: state.original.abpList,
                    rootColumnGroups: state.original.rootColumnGroups,
                    rows: rows.map((row) => row.createPlainCopy(true)),
                    rejectReasons: rejectReasons,
                };
            }
        },

        markAsUnchanged(): Fzp.ReportPart {
            if (result.isChanged) {
                for (const row of rows) {
                    row.markAsUnchanged();
                }

                state.original = result.createPlainCopy(true);
                state.originalRejectReasonsMap = new Map(state.rejectReasonsMap);
            }
            return state.original;
        },

        getRejectReason,
        setRejectReason,
        isRejectReasonChanged,
    };
    return result;
};

export const createReactiveReport = (original: Fzp.Report): Fzp.ReactiveReport => {
    const allParts: Array<Fzp.ReactiveReportPart> = [];

    const convertPart = (originalPart: Fzp.ReportPart | null): (Fzp.ReactiveReportPart | null) => {
        if (originalPart === null) {
            return null;
        } else {
            const result = createReactiveReportPart(originalPart);
            allParts.push(result);
            return result;
        }
    };

    const state = Vue.observable({
        original,
    });

    const govMbPart = convertPart(state.original.govMbPart);
    const govRbPart = convertPart(state.original.govRbPart);
    const civilMbPart = convertPart(state.original.civilMbPart);
    const civilRbPart = convertPart(state.original.civilRbPart);
    const mvdMbPart = convertPart(state.original.mvdMbPart);
    const mvdRbPart = convertPart(state.original.mvdRbPart);

    const result: Fzp.ReactiveReport = {
        get govMbPart(): (Fzp.ReactiveReportPart | null) {
            return govMbPart;
        },

        get govRbPart(): (Fzp.ReactiveReportPart | null) {
            return govRbPart;
        },

        get civilMbPart(): (Fzp.ReactiveReportPart | null) {
            return civilMbPart;
        },

        get civilRbPart(): (Fzp.ReactiveReportPart | null) {
            return civilRbPart;
        },

        get mvdMbPart(): (Fzp.ReactiveReportPart | null) {
            return mvdMbPart;
        },

        get mvdRbPart(): (Fzp.ReactiveReportPart | null) {
            return mvdRbPart;
        },

        get isChanged(): boolean {
            return (
                (govMbPart?.isChanged === true)
                ||
                (govRbPart?.isChanged === true)
                ||
                (civilMbPart?.isChanged === true)
                ||
                (civilRbPart?.isChanged === true)
                ||
                (mvdMbPart?.isChanged === true)
                ||
                (mvdRbPart?.isChanged === true)
            );
        },

        get original(): Fzp.Report {
            return state.original;
        },

        reset(): void {
            govMbPart?.reset();
            govRbPart?.reset();
            civilMbPart?.reset();
            civilRbPart?.reset();
            mvdMbPart?.reset();
            mvdRbPart?.reset();
        },

        createPlainCopy(canUseOriginals?: boolean): Fzp.Report {
            if (canUseOriginals && (!result.isChanged)) {
                return state.original;
            } else {
                return {
                    govMbPart: (govMbPart?.createPlainCopy(true) ?? null),
                    govRbPart: (govRbPart?.createPlainCopy(true) ?? null),
                    civilMbPart: (civilMbPart?.createPlainCopy(true) ?? null),
                    civilRbPart: (civilRbPart?.createPlainCopy(true) ?? null),
                    mvdMbPart: (mvdMbPart?.createPlainCopy(true) ?? null),
                    mvdRbPart: (mvdRbPart?.createPlainCopy(true) ?? null),
                };
            }
        },

        markAsUnchanged(): Fzp.Report {
            if (this.isChanged) {
                for (const part of allParts) {
                    part.markAsUnchanged();
                }

                state.original = result.createPlainCopy(true);
            }
            return state.original;
        },
    };
    return result;
};
// endregion


export const localState: Fzp.LocalState = ((): Fzp.LocalState => {
    const state = Vue.observable({
        reportId: (null as (number | null)),
        reportHasContent: false,
        expandedRowKeys: new Set<string>(),
    });

    return {
        get reportId(): number | null {
            return state.reportId;
        },
        set reportId(value) {
            state.reportId = value;
        },

        get reportHasContent(): boolean {
            return state.reportHasContent;
        },
        set reportHasContent(value) {
            state.reportHasContent = value;
        },

        isRowExpanded(key: string): boolean {
            return state.expandedRowKeys.has(key);
        },
        setRowExpanded(key: string, value: boolean) {
            const change = (action: (newExpandedRowKeys: Set<string>) => unknown): void => {
                const newExpandedRowKeys = new Set(state.expandedRowKeys);
                action(newExpandedRowKeys);
                state.expandedRowKeys = newExpandedRowKeys;
            };

            if (value) {
                if (!state.expandedRowKeys.has(key)) {
                    change((newExpandedRowKeys) => {
                        newExpandedRowKeys.add(key);
                    });
                }
            } else {
                if (state.expandedRowKeys.has(key)) {
                    change((newExpandedRowKeys) => {
                        newExpandedRowKeys.delete(key);
                    });
                }
            }
        },
        resetExpandedRows() {
            state.expandedRowKeys = new Set();
        },
    };
})();